import { getVoicePromptBlock } from './services/voiceProfile.js';

const GEMINI_API_URL = "https://generativelanguage.googleapis.com/v1/models";
const OPENAI_API_URL = "https://api.openai.com/v1/chat/completions";
const ANTHROPIC_API_URL = "https://api.anthropic.com/v1/messages";
const GROQ_API_URL = "https://api.groq.com/openai/v1/chat/completions";
const DEEPSEEK_API_URL = "https://api.deepseek.com/v1/chat/completions";

// Helper to wait/sleep (for retries if needed, though we actally just switch models)
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const callGemini = async (apiKey, prompt, model = 'gemini-2.5-flash', retries = 3) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(`${GEMINI_API_URL}/${model}:generateContent?key=${apiKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }),
      });

      if (response.status === 429) {
        if (attempt === retries) throw new Error(`Gemini 429 Rate Limit Exceeded after ${retries} retries`);
        const delay = 2000 * (attempt + 1); // Linear backoff: 2s, 4s, 6s...
        console.warn(`Gemini 429 (Rate Limit). Retrying in ${delay}ms...`);
        await sleep(delay);
        continue;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || `Gemini ${model} failed`);
      }

      const data = await response.json();
      return data.candidates[0].content.parts[0].text;
    } catch (error) {
      if (attempt === retries || !error.message.includes('429')) {
        console.warn(`Gemini (${model}) failed:`, error.message);
        throw error;
      }
    }
  }
};

const callOpenAI = async (apiKey, prompt, model = 'gpt-3.5-turbo') => {
  try {
    const response = await fetch(OPENAI_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: model,
        messages: [{ role: "user", content: prompt }],
        temperature: 0.7
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || "OpenAI failed");
    }

    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.warn("OpenAI failed:", error.message);
    throw error;
  }
};

// Generic OpenAI-compatible caller (used by Groq + DeepSeek)
const callOpenAICompatible = async (url, apiKey, prompt, model, providerName) => {
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model,
        messages: [{ role: "user", content: prompt }],
        temperature: 0.7
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `${providerName} failed`);
    }

    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.warn(`${providerName} failed:`, error.message);
    throw error;
  }
};

const callGroq = (apiKey, prompt, model = 'llama-3.3-70b-versatile') =>
  callOpenAICompatible(GROQ_API_URL, apiKey, prompt, model, 'Groq');

const callDeepSeek = (apiKey, prompt, model = 'deepseek-chat') =>
  callOpenAICompatible(DEEPSEEK_API_URL, apiKey, prompt, model, 'DeepSeek');

const callAnthropic = async (apiKey, prompt, model = 'claude-3-haiku-20240307') => {
  try {
    const response = await fetch(ANTHROPIC_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerously-allow-browser": "true" // Required for client-side calls
      },
      body: JSON.stringify({
        model: model,
        max_tokens: 1024,
        messages: [{ role: "user", content: prompt }]
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || "Anthropic failed");
    }

    const data = await response.json();
    return data.content[0].text;
  } catch (error) {
    console.warn("Anthropic failed:", error.message);
    throw error;
  }
};

export const generateAIContent = async (apiConfig, prompt) => {
  // Normalize config: if string, treat as Gemini key (backward compatibility)
  const config = typeof apiConfig === 'string' ? { gemini: apiConfig } : apiConfig;

  const hasAnyKey = config && (config.gemini || config.openai || config.anthropic || config.groq || config.deepseek);
  if (!hasAnyKey) {
    throw new Error("No API Key provided for any provider");
  }

  const errors = [];

  // 1. Gemini (Primary, free tier)
  if (config.gemini) {
    const geminiModels = ['gemini-2.5-flash'];
    for (const model of geminiModels) {
      try {
        return await callGemini(config.gemini, prompt, model);
      } catch (e) {
        errors.push(`Gemini ${model}: ${e.message}`);
        if (e.message.includes('API key not valid') || e.message.includes('401')) break;
      }
    }
  }

  // 2. Groq (cheap + sub-second latency)
  if (config.groq) {
    try {
      return await callGroq(config.groq, prompt);
    } catch (e) {
      try {
        return await callGroq(config.groq, prompt, 'llama-3.1-8b-instant');
      } catch (e2) {
        errors.push(`Groq: ${e.message}`);
      }
    }
  }

  // 3. DeepSeek (~10x cheaper than gpt-4o-mini)
  if (config.deepseek) {
    try {
      return await callDeepSeek(config.deepseek, prompt);
    } catch (e) {
      errors.push(`DeepSeek: ${e.message}`);
    }
  }

  // 4. OpenAI
  if (config.openai) {
    try {
      return await callOpenAI(config.openai, prompt, 'gpt-4o-mini');
    } catch (e) {
      try {
        return await callOpenAI(config.openai, prompt, 'gpt-3.5-turbo');
      } catch (e2) {
        errors.push(`OpenAI: ${e.message}`);
      }
    }
  }

  // 5. Anthropic
  if (config.anthropic) {
    try {
      return await callAnthropic(config.anthropic, prompt);
    } catch (e) {
      errors.push(`Anthropic: ${e.message}`);
    }
  }

  throw new Error(`All AI providers failed. Details: \n${errors.join('\n')}`);
};

// Hard guardrail prepended to every generation prompt. Borrowed from
// santifer/career-ops's source-of-truth boundary: the AI may REFORMULATE
// what is already present in the user's resume, but must never INVENT
// skills, companies, dates, or quantitative metrics. Reduces hallucination
// complaints and protects users from putting fictional credentials on a
// document recruiters will verify.
const STRICT_FACTUALITY = `
HARD RULES — APPLY TO EVERYTHING YOU WRITE:
1. Do NOT invent skills, technologies, companies, job titles, dates, degrees, or schools that are not present in the provided resume data.
2. Do NOT invent specific numbers, percentages, dollar amounts, team sizes, or time durations. Preserve numbers that are already present verbatim.
3. You may REFORMULATE existing content for clarity, impact, and tone. You may NOT add facts.
4. If the existing content lacks a metric, use qualitative impact language ("significantly", "substantially") instead of fabricating "[X]%" placeholders or made-up numbers.
5. If asked to highlight a skill not in the resume, refuse and suggest the user add it themselves.
`;

export const getPrompts = (type, data, language = 'English') => {
  const langInstruction = `IMPORTANT: Provide the response content in ${language}. However, keep all JSON keys in English.`;
  const factuality = STRICT_FACTUALITY;
  // Empty unless the user has saved a voice profile. Injected into prose-
  // generating prompts so AI output sounds like the user, not like an LLM.
  const voice = getVoicePromptBlock();

  switch (type) {
    case 'summary':
      return `${factuality}${voice}\n\nWrite a professional resume summary for a ${data.role || "professional"} with expertise in ${data.skills || "various fields"}. Keep it concise (2-3 sentences), impactful, and use strong action verbs. Do not include any preamble or markdown formatting, just the summary text. ${langInstruction}`;

    case 'experience':
      return `${factuality}${voice}\n\nWrite 3-4 professional bullet points for a resume experience section.
            Role: ${data.role}
            Company: ${data.company}
            Context: ${data.description || "General responsibilities"}

            Focus on achievements, metrics, and impact. Start each bullet with a strong action verb. Do not include any preamble or markdown formatting, just the bullet points. ${langInstruction}`;

    case 'job-match':
      return `${factuality}\n\nYou are a professional resume consultant. Analyze how well this resume matches the job description.

RESUME:
Name: ${data.resume.personal.fullName}
Summary: ${data.resume.personal.summary}
Skills: ${data.resume.skills.join(', ')}
Experience: ${JSON.stringify(data.resume.experience.map(e => ({ id: e.id, role: e.role, company: e.company, description: e.description })))}

JOB DESCRIPTION:
${data.jobDescription}

${langInstruction}
Provide a JSON response with this exact structure (no markdown, just raw JSON):
{
  "matchScore": <number 0-100>,
  "matchingSkills": ["skill1", "skill2"],
  "missingSkills": ["skill1", "skill2"],
  "suggestions": ["suggestion1", "suggestion2"],
  "tailoredSummary": "A professional summary tailored for this specific job",
  "skillsToAdd": ["skill1", "skill2"],
  "tailoredExperience": [
    { "id": <experience_id>, "tailoredDescription": "Rewritten bullet points emphasizing relevance to the job..." }
  ]
}`;

    case 'skill-enhancement':
      return `${factuality}\n\nYou are a professional resume writer. The user wants to add the skill "${data.skill}" to their resume.
            
            1. Write ONE concise, impactful sentence incorporating "${data.skill}" suitable for a professional summary.
            2. Write ONE strong, metric-driven bullet point demonstrating experience with "${data.skill}".
            
            ${langInstruction}
            Provide a JSON response with this exact structure (no markdown, just raw JSON):
            {
              "summarySentence": "The summary sentence...",
              "experienceBullet": "The experience bullet point..."
            }`;

    case 'cover-letter':
      return `${factuality}${voice}\n\nYou are a professional career coach. Write a compelling cover letter for this candidate.

RESUME:
Name: ${data.resume.personal.fullName}
Role: ${data.resume.personal.summary}
Skills: ${data.resume.skills.join(', ')}
Experience: ${JSON.stringify(data.resume.experience.map(e => ({ role: e.role, company: e.company, desc: e.description })))}

JOB DESCRIPTION:
${data.jobDescription}

INSTRUCTIONS:
- Tone: Professional, confident, and enthusiastic.
- Structure: Standard business letter format.
- Content: Connect the candidate's specific experiences to the job requirements.
- Length: Concise (3-4 paragraphs).

${langInstruction}
Provide a JSON response with this exact structure (no markdown, just raw JSON):
{
  "subject": "Email Subject Line",
  "content": "The full cover letter text..."
}`;

    case 'interview-prep':
      return `${factuality}

You are a technical hiring manager. Based on this resume and job description, generate interview questions.

RESUME:
Role: ${data.resume.personal.summary}
Skills: ${data.resume.skills.join(', ')}
Experience: ${JSON.stringify(data.resume.experience.map(e => ({ role: e.role, company: e.company, desc: e.description })))}

JOB DESCRIPTION:
${data.jobDescription}

INSTRUCTIONS:
Generate 5 questions:
- 3 Technical Questions: Specific to the technologies/skills in the job description and resume.
- 2 Behavioral Questions: Focused on soft skills, culture fit, or past experiences.

For EACH question, provide a "Suggested Answer" using the STAR + Reflection method:
- Situation: the context (drawn from the candidate's actual resume experience)
- Task: what needed to be done
- Action: what the candidate specifically did (use "I", not "we")
- Result: the measurable outcome (preserve any numbers in the resume verbatim; do NOT invent metrics)
- Reflection: what the candidate learned or what they'd do differently — this is the senior-coded coda that demonstrates self-awareness, the part most candidates miss

If the candidate's experience does not directly map, suggest a plausible angle based on their listed skills WITHOUT inventing companies or roles.

${langInstruction}
Provide a JSON response with this exact structure (no markdown, just raw JSON):
{
  "questions": [
    {
      "type": "Technical" or "Behavioral",
      "question": "The question text...",
      "suggestedAnswer": "Situation: ... Task: ... Action: ... Result: ... Reflection: ..."
    }
  ]
}`;

    case 'parse-resume':
      return `You are a data extraction expert. Parse the following resume text into a structured JSON format.
            
            TEXT:
            ${data.text}
            
            INSTRUCTIONS:
            1. Detect the language of the text automatically.
            2. Extract Personal Details (Name, Email, Phone, Location, Summary).
            3. Extract Experience (Role, Company, Dates, Description).
            4. Extract Education (School, Degree, Year).
            5. Extract Skills (as a list of strings).
            6. Map foreign language sections to the correct English keys below (e.g. "Experiencia" -> "experience").
            7. Keep the *values* in the original language (do not translate the content, just structure it).
            
            Provide a JSON response with this exact structure (no markdown, just raw JSON):
            {
              "personal": {
                "fullName": "Name",
                "email": "Email",
                "phone": "Phone",
                "location": "City, Country",
                "summary": "Summary text..."
              },
              "experience": [
                { "id": 1, "role": "Job Title", "company": "Company Name", "startDate": "Start Date", "endDate": "End Date", "description": "Bullet points..." }
              ],
              "education": [
                { "id": 1, "school": "University", "degree": "Degree", "year": "Year" }
              ],
              "skills": ["Skill1", "Skill2"]
            }`;

    case 'translate-resume':
      return `You are a professional translator. Translate the following resume JSON content into ${language}.
            
            RESUME DATA:
            ${JSON.stringify(data)}
            
            INSTRUCTIONS:
            1. Translate ALL content values (descriptions, roles, summaries, skills, custom section items) into ${language}.
            2. Do NOT translate proper nouns (Company names, University names, Locations) unless there is a standard localized name.
            3. Do NOT translate or change any JSON keys (keep them exactly as they are).
            4. Keep the exact same JSON structure.
            
            ${langInstruction}
            Provide the translated JSON response with the exact same structure (no markdown, just raw JSON).`;

    case 'roast-resume':
      return `You are a brutally honest, critical, but constructive career coach. Your job is to "Roast" this resume against the job description.
            
            JOB DESCRIPTION:
            ${data.jobDescription}
            
            RESUME:
            ${JSON.stringify(data.resume)}
            
            INSTRUCTIONS:
            1. Analyze the resume specifically for this job.
            2. Be direct and critical. Don't sugarcoat.
            3. Point out vague language, bad formatting (implied), lack of metrics, and relevance issues.
            4. Provide a "Brutal Score" from 1-10 (be strict).
            5. Provide a witty/harsh "One Liner" summary.
            
            ${langInstruction} (Translate the output to this language).
            
            Provide a JSON response with this structure:
            {
              "score": 5,
              "oneLiner": "This resume is as generic as a store brand cereal.",
              "compliments": ["Good education section", "Clear contact info"],
              "criticisms": ["Bullet points lack impact", "Too many buzzwords"],
              "suggestedFixes": ["Add numbers to your sales experience", "Remove 'Hard worker' from skills"]
            }`;

    case 'improve-entire-resume':
      return `${factuality}\n\nYou are an expert resume writer. Improve the following resume content to be more impactful, professional, and metric-driven.

            RESUME DATA:
            ${JSON.stringify(data)}

            INSTRUCTIONS:
            1.  **Summary**: Rewrite the summary to be punchy, highlighting key skills and years of experience.
            2.  **Experience**: Rewrite the bullet points for *every* job.
                *   Use strong action verbs.
                *   **PRESERVE existing numbers/metrics.**
                *   Focus on achievements. Avoid adding generic placeholders like "[X]%" unless essential.
            3.  **Skills**: Suggest 3-5 relevant skills to add based on the experience descriptions.

            ${langInstruction}
            Provide a JSON response with this exact structure (no markdown, just raw JSON):
            {
              "personal": {
                 "summary": "Impactful summary..."
              },
              "experience": [
                 { "id": <original_id>, "description": "Improved bullet points..." }
              ],
              "suggestedSkills": ["Skill1", "Skill2"]
            }`;

    case 'improve-summary':
      return `${factuality}${voice}\n\nYou are an expert resume writer. Rewrite the following professional summary to be more impactful, concise, and professional.

            CURRENT SUMMARY:
            ${data.currentSummary || "No summary provided"}

            CONTEXT:
            Role: ${data.role}
            Skills: ${data.skills}

            INSTRUCTIONS:
            - Make it punchy and results-oriented.
            - Focus on the value the candidate brings.
            - Avoid generic buzzwords.
            - Keep it to 3-4 powerful sentences.
            - **If the Current Summary is empty or "No summary provided", GENERATE a new professional summary based on the Role and Skills.**

            ${langInstruction}
            Provide a JSON response with this exact structure (no markdown, just raw JSON):
            {
              "summary": "The rewritten summary text..."
            }`;

    case 'improve-experience-entry':
      return `${factuality}${voice}\n\nYou are an expert resume writer. Rewrite the bullet points for this specific job entry to be more impactful and metric-driven.

            JOB DETAILS:
            Role: ${data.role}
            Company: ${data.company}
            Current Bullets: 
            ${data.description}

            INSTRUCTIONS:
            - Use strong action verbs (e.g., spearheaded, optimized, engineered).
            - **PRESERVE specific numbers and metrics** from the original text. Do NOT verify or change them.
            - If the original text lacks specific numbers, focus on the *qualitative* impact (e.g., "significantly improved performance" instead of "[X]% improvement").
            - Do NOT add placeholders like "[X]" or "[Y]" unless the sentence structure absolutely requires a quantification that is missing.
            - Focus on achievements/outcomes rather than just duties.
            - Maintain the same number of bullet points if possible, or consolidate weak ones.

            ${langInstruction}
            Provide a JSON response with this exact structure (no markdown, just raw JSON):
            {
              "description": "The rewritten bullet points..."
            }`;

    case 'mock-interview-question':
      return `${factuality}

You are interviewing a candidate for a job. Your persona: ${data.persona.name} (${data.persona.description}).

CANDIDATE RESUME:
${JSON.stringify({
        summary: data.resume.personal.summary,
        skills: data.resume.skills,
        experience: data.resume.experience.map(e => ({ role: e.role, company: e.company, desc: e.description }))
      })}

${data.jobDescription ? `TARGET JOB:\n${data.jobDescription}\n` : ''}

CONVERSATION HISTORY:
${(data.history || []).map((h, i) => `Q${i + 1}: ${h.question}\nA${i + 1}: ${h.answer || '(skipped)'}${h.feedback ? `\nFeedback: ${h.feedback}` : ''}`).join('\n\n') || '(none yet)'}

INSTRUCTIONS:
- Ask ONE realistic interview question consistent with your persona.
- ${data.history?.length > 0 ? 'You may ask a follow-up to the previous answer if it left key details unexplored, OR move to a new question covering a different competency.' : 'Open with a strong but not overly hard first question.'}
- Stay in character. Be professional and probing.
- Cover competencies progressively across the session: communication, technical depth, problem-solving, leadership, culture fit, ownership.

${langInstruction}
Return ONLY raw JSON (no markdown):
{
  "question": "The interview question...",
  "competency": "one of: communication, technical, problem-solving, leadership, culture-fit, ownership",
  "isFollowUp": ${data.history?.length > 0 ? 'true or false' : 'false'},
  "hint": "Brief STAR+Reflection hint for the candidate (1 sentence): mention that 'Reflection' (what you'd do differently) lifts the answer to senior-level."
}`;

    case 'mock-interview-score':
      return `${factuality}

You are evaluating a candidate's interview answer. Be honest but constructive.

PERSONA: ${data.persona.name} (${data.persona.description})

QUESTION: ${data.question}
COMPETENCY BEING ASSESSED: ${data.competency}

CANDIDATE'S ANSWER:
${data.answer || '(skipped)'}

CANDIDATE RESUME CONTEXT:
${JSON.stringify({
        summary: data.resume.personal.summary,
        skills: data.resume.skills
      })}

INSTRUCTIONS:
- Score the answer 0–10 against the competency.
- Identify strengths (specific phrases that worked) and weaknesses.
- Provide a model STAR + Reflection answer the candidate could have given, based on their actual resume. Reflection = what they learned or would do differently — this is the senior-coded coda most candidates skip.
- Suggest a one-line improvement.
- Score additional competencies if relevant (communication is almost always scored).
- The model answer must use only facts present in the candidate's resume. Do not invent companies, metrics, or roles.

${langInstruction}
Return ONLY raw JSON:
{
  "score": <0-10>,
  "competencyScores": { "communication": <0-10>, "technical": <0-10 or null>, "problem-solving": <0-10 or null>, "leadership": <0-10 or null>, "culture-fit": <0-10 or null>, "ownership": <0-10 or null> },
  "strengths": ["specific phrase or behavior that worked"],
  "weaknesses": ["specific gap"],
  "modelAnswer": "S: ... T: ... A: ... R: ... Reflection: ...",
  "oneLineImprovement": "Try …"
}`;

    case 'mock-interview-summary':
      return `Summarize a mock interview session.

PERSONA: ${data.persona.name}
SESSION:
${data.session.map((s, i) => `Q${i + 1} (${s.competency}): ${s.question}\nAnswer: ${s.answer || '(skipped)'}\nScore: ${s.score}/10\nFeedback: ${s.feedback}`).join('\n\n')}

INSTRUCTIONS:
- Give an overall verdict (Hire / No Hire / Borderline).
- List top 3 strengths across the session.
- List top 3 areas to work on.
- Recommend the candidate's next prep focus.

${langInstruction}
Return ONLY raw JSON:
{
  "verdict": "Hire | No Hire | Borderline",
  "overallScore": <0-100>,
  "topStrengths": ["…"],
  "topGaps": ["…"],
  "nextPrepFocus": "…"
}`;

    case 'salary-negotiation':
      return `${factuality}${voice}

You are a senior compensation negotiation coach. Produce a complete negotiation playbook for this candidate.

CANDIDATE:
Role they are negotiating: ${data.targetRole}
Seniority context (from resume): ${JSON.stringify(data.resume.experience.slice(0, 3).map(e => ({ role: e.role, company: e.company })))}
Years of experience hint: ${data.yearsExperience || 'unspecified'}
Skills: ${(data.resume.skills || []).slice(0, 15).join(', ')}

OFFER ON THE TABLE:
${data.offer || 'No formal offer yet — pre-offer negotiation'}

CANDIDATE'S TARGET: ${data.targetSalary || 'unspecified'}
CANDIDATE'S CURRENT/PRIOR: ${data.currentSalary || 'unspecified'}
LEVERAGE: ${data.leverage || 'unspecified'}
WALK-AWAY POINT: ${data.walkAway || 'unspecified'}
LOCATION / MARKET: ${data.location || 'unspecified'}

PRODUCE four practical artifacts the candidate can use this week:

1. **Recruiter script** — what to say to the recruiter on the FIRST salary call. Should anchor high, ask for the band, defer commitment.
2. **Hiring-manager script** — what to say to the hiring manager if/when asked about expectations directly.
3. **Counter-offer ladder** — 3 specific counter levels (aggressive, default, fallback) with exact dollar/non-cash asks (signing bonus, equity refresh, vacation, start date, title). Be specific to the candidate's seniority — do not invent industry data.
4. **Red flags to watch for** — 3 phrases / behaviors from the employer that indicate the offer won't move and walking away is correct.

GUARDRAILS:
- Do NOT invent salary band data or "industry averages" you don't have data for. If data is unspecified, write scripts that PROBE for the band rather than asserting one.
- Do not invent the candidate's skills or accomplishments.
- Tone: confident, specific, not pleading.

${langInstruction}
Return ONLY raw JSON (no markdown):
{
  "recruiterScript": "What to say verbatim, in quotes…",
  "hiringManagerScript": "What to say verbatim, in quotes…",
  "counterOfferLadder": [
    { "level": "Aggressive", "ask": "Specific ask…", "rationale": "Why this is justified for their level" },
    { "level": "Default", "ask": "…", "rationale": "…" },
    { "level": "Fallback", "ask": "…", "rationale": "…" }
  ],
  "redFlags": ["specific employer phrase or behavior", "…", "…"],
  "topThreeMoves": ["The 3 things to actually do this week"]
}`;

    case 'extract-search-terms':
      return `You are a career consultant. Based on the following resume data, suggest the 3 most relevant job titles and 1 location (if inferable, otherwise "Remote") to search for.

            RESUME:
            ${JSON.stringify(data)}

            INSTRUCTIONS:
            1. Analyze the skills, experience, and summary.
            2. Infer the candidate's likely target roles (e.g. "Senior React Developer", "Frontend Engineer").
            3. Infer the location if mentioned in personal details or recent experience (City, Country), otherwise "Remote".
            
            ${langInstruction}
            Provide a JSON response with this exact structure (no markdown, just raw JSON):
            {
              "keywords": "Job Title 1, Job Title 2",
              "location": "City, Country or Remote"
            }`;

    default:
      return "";
  }
};
